/**
 * Password validation utility matching backend Laravel validation rules:
 * - nullable (optional - password can be empty)
 * - min:8 (minimum 8 characters)
 * - confirmed (password confirmation must match)
 * - regex:/[A-Z]/ (at least one uppercase letter)
 * - regex:/[a-z]/ (at least one lowercase letter)
 * - regex:/[0-9]/ (at least one number)
 * - regex:/[@$!%*#?&]/ (at least one special character)
 */

/**
 * Get password validation rules for Ant Design Form
 * @param {Object} options - Configuration options
 * @param {boolean} options.required - Whether password is required (default: false, since backend is nullable)
 * @param {Function} options.t - Translation function (optional)
 * @returns {Array} Array of validation rules for Ant Design Form
 */
export const getPasswordValidationRules = ({ required = false, t = (key) => key } = {}) => {
  return [
    // Required validation (only if explicitly set)
    ...(required ? [{ required: true, message: t('required') }] : []),
    
    // Custom validator for password strength
    {
      validator(_, value) {
        // If password is empty and not required, allow it (nullable)
        if (!value || value.trim() === '') {
          if (required) {
            return Promise.reject(new Error(t('required')));
          }
          return Promise.resolve();
        }

        const password = value.toString();

        // Minimum length validation
        if (password.length < 8) {
          return Promise.reject(new Error(t('password.min.8.characters') || 'Password must be at least 8 characters'));
        }

        // Uppercase letter validation
        if (!/[A-Z]/.test(password)) {
          return Promise.reject(new Error(t('password.must.contain.uppercase') || 'Password must contain at least one uppercase letter'));
        }

        // Lowercase letter validation
        if (!/[a-z]/.test(password)) {
          return Promise.reject(new Error(t('password.must.contain.lowercase') || 'Password must contain at least one lowercase letter'));
        }

        // Number validation
        if (!/[0-9]/.test(password)) {
          return Promise.reject(new Error(t('password.must.contain.number') || 'Password must contain at least one number'));
        }

        // Special character validation
        if (!/[@$!%*#?&]/.test(password)) {
          return Promise.reject(new Error(t('password.must.contain.special') || 'Password must contain at least one special character (@$!%*#?&)'));
        }

        return Promise.resolve();
      },
    },
  ];
};

/**
 * Get password confirmation validation rules for Ant Design Form
 * @param {Object} options - Configuration options
 * @param {boolean} options.required - Whether password confirmation is required (default: false)
 * @param {Function} options.t - Translation function (optional)
 * @returns {Array} Array of validation rules for Ant Design Form
 */
export const getPasswordConfirmationValidationRules = ({ required = false, t = (key) => key } = {}) => {
  return [
    // Required validation (only if explicitly set)
    ...(required ? [{ required: true, message: t('required') }] : []),
    
    // Match password validation
    ({ getFieldValue }) => ({
      validator(rule, value) {
        const password = getFieldValue('password');
        
        // If password is empty and not required, allow empty confirmation
        if (!password && !required) {
          return Promise.resolve();
        }
        
        // If password exists, confirmation must match
        if (!value && password) {
          return Promise.reject(new Error(t('required')));
        }
        
        if (!value || password === value) {
          return Promise.resolve();
        }
        
        return Promise.reject(new Error(t('two.passwords.dont.match') || t('passwords.do.not.match') || 'Passwords do not match'));
      },
    }),
  ];
};

export default {
  getPasswordValidationRules,
  getPasswordConfirmationValidationRules,
};


